import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";
import type {
  MikrotikJob,
  MikrotikJobCounts,
  PagedJobs,
} from "./schemas";

const KEY = "mikrotik-jobs";

export function useJobCounts(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [KEY, "counts"],
    queryFn: () =>
      apiCall<MikrotikJobCounts>("get", "/api/v1/mikrotik-jobs/counts"),
    refetchInterval: 10000,
    refetchOnWindowFocus: true,
    enabled: opts.enabled ?? true,
  });
}

/** Poll un job por id. refetchInterval se ajusta según status.
 *
 * dev119: polling adaptativo — `running` → 1.5s para mostrar progreso fluido;
 * `queued` → pollIntervalMs (default 2s); `completed`/`error` → para. */
export function useJob(jobId: string | null, pollIntervalMs = 2000) {
  return useQuery({
    queryKey: [KEY, "detail", jobId],
    queryFn: () =>
      apiCall<MikrotikJob>("get", `/api/v1/mikrotik-jobs/${jobId}`),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data as MikrotikJob | undefined;
      if (!data) return pollIntervalMs;
      if (data.status === "completed" || data.status === "error") {
        return false; // para de polear
      }
      if (data.status === "running") {
        return 1500; // dev119: más rápido cuando hay progreso en vivo
      }
      return pollIntervalMs;
    },
    refetchOnWindowFocus: false,
  });
}

export type JobsFilters = {
  page?: number;
  size?: number;
  router_id?: string;
  status?: string;
  action?: string;
  related_entity?: string;
  related_id?: string;
};

export function useListJobs(filters: JobsFilters) {
  return useQuery({
    queryKey: [KEY, "list", filters],
    queryFn: () => {
      const p: Record<string, string | number> = {
        page: filters.page ?? 1,
        size: filters.size ?? 50,
      };
      if (filters.router_id) p.router_id = filters.router_id;
      if (filters.status) p.status = filters.status;
      if (filters.action) p.action = filters.action;
      if (filters.related_entity) p.related_entity = filters.related_entity;
      if (filters.related_id) p.related_id = filters.related_id;
      return apiCall<PagedJobs>("get", "/api/v1/mikrotik-jobs", {
        searchParams: p,
      });
    },
    placeholderData: (prev) => prev,
    // dev119: si hay algún job running visible, baja a 2s para que la
    // mini-barra de progreso se vea fluida. Sin running, default 5s.
    refetchInterval: (query) => {
      const data = query.state.data as PagedJobs | undefined;
      if (data?.items?.some((j) => j.status === "running")) return 2000;
      return 5000;
    },
  });
}

/** F16.10: dispara manualmente replay_pending + recover_stuck en el backend. */
export function useProcesarPendientes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiCall<{ reenqueued: number; recovered_stuck: number }>(
        "post", "/api/v1/mikrotik-jobs/procesar-pendientes", { json: {} },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
    },
  });
}
